import { useCallback, useEffect, useState } from 'react';
import { api, setActiveTenant, type Me, type Meta, type Source } from './api';
import { Button, Dot, Empty, ErrorNote, Field as FormField, Input, Select, healthOf, relativeTime } from './components/ui';
import { SourceWorkbench } from './components/SourceWorkbench';
import { OperatorPanel } from './components/OperatorPanel';
import { ActionsPanel } from './components/ActionsPanel';
import { BrokersPanel } from './components/BrokersPanel';

function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.signIn(email, password);
      onSignedIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="h-full grid place-items-center p-6">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4">
        <div>
          <h1 className="text-xl font-medium">LoxLink</h1>
          <p className="text-sm text-ink-500 mt-1">Web data into Loxone Miniservers.</p>
        </div>
        <FormField label="Email">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required />
        </FormField>
        <FormField label="Password">
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </FormField>
        {error && <ErrorNote>{error}</ErrorNote>}
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
        <p className="text-xs text-ink-500">Registration is by invitation.</p>
      </form>
    </div>
  );
}

function NewSourceForm({ meta, onCreated, onCancel }: { meta: Meta; onCreated: (s: Source) => void; onCancel: () => void }) {
  const [form, setForm] = useState({ name: '', slug: '', url: '', kind: 'json', timezone: '', interval_seconds: 300, mode: 'poll', broker_id: '', topic: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onCreated(
        await api.createSource({
          ...form,
          kind: form.kind as Source['kind'],
          mode: form.mode as Source['mode'],
          interval_seconds: Number(form.interval_seconds),
        })
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  // Derive a slug from the name so the common case needs no thought.
  const setName = (name: string): void =>
    setForm((f) => ({
      ...f,
      name,
      slug: f.slug || name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 48),
    }));

  return (
    <form onSubmit={submit} className="space-y-3 max-w-2xl">
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Name">
          <Input value={form.name} onChange={(e) => setName(e.target.value)} placeholder="Electricity price" required />
        </FormField>
        <FormField label="Slug" hint="appears in the /out URL">
          <Input mono value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required />
        </FormField>
      </div>
      <FormField
        label="How does data arrive?"
        hint={
          form.mode === 'push'
            ? 'Another system posts to a URL LoxLink gives you. Nothing is fetched.'
            : 'LoxLink fetches this URL on a schedule.'
        }
      >
        <Select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
          <option value="poll">Poll — LoxLink fetches a URL</option>
          <option value="push">Push — another system sends it to us</option>
          <option value="mqtt">MQTT — subscribe to a topic</option>
        </Select>
      </FormField>
      {form.mode === 'mqtt' && (
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Broker" hint="Add one in the Brokers view first">
            <Input
              mono
              value={form.broker_id}
              onChange={(e) => setForm({ ...form, broker_id: e.target.value })}
              placeholder="broker id"
              required
            />
          </FormField>
          <FormField label="Topic" hint="+ matches one level, # the rest">
            <Input
              mono
              value={form.topic}
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
              placeholder="home/kitchen/temp"
              required
            />
          </FormField>
        </div>
      )}
      {form.mode === 'poll' && (
        <FormField label="URL">
          <Input
            mono
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            placeholder="https://api.example.com/data"
            required
          />
        </FormField>
      )}
      <div className="grid grid-cols-3 gap-3">
        <FormField label="Format">
          <Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            {meta.sourceKinds.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Interval (s)" hint={`minimum ${meta.minIntervalSeconds}`}>
          <Input
            type="number"
            value={form.interval_seconds}
            onChange={(e) => setForm({ ...form, interval_seconds: Number(e.target.value) })}
          />
        </FormField>
        <FormField label="Timezone" hint="only for timestamps with no offset">
          <Input
            mono
            value={form.timezone}
            placeholder="Europe/Zagreb"
            onChange={(e) => setForm({ ...form, timezone: e.target.value })}
          />
        </FormField>
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? 'Creating…' : 'Create source'}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export default function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [sources, setSources] = useState<Source[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [operatorView, setOperatorView] = useState(false);
  const [actionsView, setActionsView] = useState(false);
  const [brokersView, setBrokersView] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSession = useCallback(async () => {
    setLoading(true);
    try {
      const [session, metaData] = await Promise.all([api.me(), api.meta()]);
      setMe(session);
      setMeta(metaData);
      if (session.activeTenantId) setActiveTenant(session.activeTenantId);
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSources = useCallback(async () => {
    try {
      const list = await api.sources();
      setSources(list);
      setError(null);
      setSelected((current) => current ?? list[0]?.id ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  useEffect(() => {
    if (me?.activeTenantId) void loadSources();
  }, [me, loadSources]);

  // Health and values age visibly; refresh so an open window is not quietly wrong.
  useEffect(() => {
    if (!me) return;
    const timer = setInterval(() => void loadSources(), 20_000);
    return () => clearInterval(timer);
  }, [me, loadSources]);

  if (loading) return <div className="h-full grid place-items-center text-ink-500">Loading…</div>;
  if (!me) return <SignIn onSignedIn={() => void loadSession()} />;

  const current = sources.find((s) => s.id === selected) ?? null;
  const canEdit = me.role === 'admin' || me.user.isOperator;

  return (
    <div className="h-full flex flex-col">
      <header className="flex items-center gap-4 px-4 py-2.5 border-b border-ink-800 shrink-0">
        <span className="font-medium">LoxLink</span>

        {me.memberships.length > 1 ? (
          <Select
            value={me.activeTenantId ?? ''}
            onChange={(e) => {
              setActiveTenant(e.target.value);
              setSelected(null);
              void loadSources();
              setMe({ ...me, activeTenantId: e.target.value });
            }}
            className="w-auto"
          >
            {me.memberships.map((m) => (
              <option key={m.tenantId} value={m.tenantId}>
                {m.tenantName}
              </option>
            ))}
          </Select>
        ) : (
          <span className="text-sm text-ink-500">{me.memberships[0]?.tenantName ?? 'no installation'}</span>
        )}

        <div className="ml-auto flex items-center gap-3 text-sm text-ink-500">
          <Button variant="ghost" onClick={() => { setBrokersView(!brokersView); setActionsView(false); setOperatorView(false); }}>
            {brokersView ? 'Back' : 'Brokers'}
          </Button>
          <Button variant="ghost" onClick={() => { setActionsView(!actionsView); setBrokersView(false); setOperatorView(false); }}>
            {actionsView ? 'Back' : 'Actions'}
          </Button>
          {me.user.isOperator && (
            <Button variant="ghost" onClick={() => setOperatorView(!operatorView)}>
              {operatorView ? 'Back' : 'All installations'}
            </Button>
          )}
          <span>
            {me.user.email}
            {me.user.isOperator && <span className="text-accent"> · operator</span>}
            {me.role === 'viewer' && <span> · read-only</span>}
          </span>
          <Button
            variant="ghost"
            onClick={() => {
              void api.signOut().finally(() => setMe(null));
            }}
          >
            Sign out
          </Button>
        </div>
      </header>

      {me.memberships.length === 0 ? (
        <div className="p-6">
          <Empty>This account is not a member of any installation yet.</Empty>
        </div>
      ) : (
        <div className="flex-1 flex min-h-0">
          <aside className="w-64 border-r border-ink-800 flex flex-col shrink-0">
            <div className="p-3 border-b border-ink-800">
              {canEdit && (
                <Button
                  variant="primary"
                  onClick={() => {
                    setCreating(true);
                    setSelected(null);
                  }}
                >
                  New source
                </Button>
              )}
            </div>
            <nav className="flex-1 overflow-auto">
              {sources.length === 0 ? (
                <Empty>No sources yet.</Empty>
              ) : (
                sources.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => {
                      setSelected(s.id);
                      setCreating(false);
                    }}
                    className={`w-full text-left px-3 py-2 border-b border-ink-800/60 hover:bg-ink-900 ${
                      selected === s.id ? 'bg-ink-900' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Dot health={healthOf(s)} />
                      <span className="text-sm truncate flex-1">{s.name}</span>
                    </div>
                    <div className="text-xs text-ink-500 mt-0.5 pl-4">
                      {s.fields.length} field{s.fields.length === 1 ? '' : 's'} · {relativeTime(s.last_success_at)}
                    </div>
                  </button>
                ))
              )}
            </nav>
          </aside>

          <main className="flex-1 overflow-auto p-4 min-w-0">
            {error && <ErrorNote>{error}</ErrorNote>}
            {brokersView ? (
              <BrokersPanel canEdit={canEdit} />
            ) : actionsView ? (
              <ActionsPanel canEdit={canEdit} publicUrl={meta?.publicUrl ?? null} />
            ) : operatorView ? (
              <OperatorPanel onClose={() => setOperatorView(false)} />
            ) : creating && meta ? (
              <div className="space-y-4">
                <h1 className="text-lg font-medium">New source</h1>
                <NewSourceForm
                  meta={meta}
                  onCancel={() => setCreating(false)}
                  onCreated={(s) => {
                    setCreating(false);
                    void loadSources().then(() => setSelected(s.id));
                  }}
                />
              </div>
            ) : current && meta ? (
              <SourceWorkbench
                source={current}
                meta={meta}
                canEdit={canEdit}
                onChanged={() => void loadSources()}
                onDeleted={() => {
                  setSelected(null);
                  void loadSources();
                }}
              />
            ) : (
              <Empty>Select a source, or create one.</Empty>
            )}
          </main>
        </div>
      )}
    </div>
  );
}
